import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { BACKEND_URL } from '../config';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';

// Composant Toast moderne
const Toast = ({ message, type, onClose }) => {
  const bgColors = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-amber-500',
    info: 'bg-blue-500'
  };
  const icons = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ'
  };

  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-white ${bgColors[type]} animate-slide-in`}>
      <span className="text-lg font-bold">{icons[type]}</span>
      <span className="font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-80">✕</button>
      <style>{`
        @keyframes slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-slide-in { animation: slide-in 0.3s ease-out; }
      `}</style>
    </div>
  );
};

const BusinessShopOrders = () => {
  const routeBase = useWorkspaceBasePath();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [toast, setToast] = useState(null);
  const [hasAccess, setHasAccess] = useState(true);
  
  // État pour le modal de confirmation de statut
  const [confirmModal, setConfirmModal] = useState(null);

  // Fonction pour afficher un toast
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  useEffect(() => {
    checkAccessAndLoadOrders();
  }, []);

  useEffect(() => {
    if (hasAccess) {
      loadOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page, searchQuery, hasAccess]);

  const checkAccessAndLoadOrders = async () => {
    try {
      const accessRes = await api.get('/business/access');
      
      if (!accessRes.data.has_shop_module) {
        setHasAccess(false);
        setLoading(false);
        return;
      }
      
      setHasAccess(true);
    } catch (err) {
      console.error(err);
      setHasAccess(false);
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      params.append('page', page);
      
      const res = await api.get(`/shop/orders?${params.toString()}`);
      if (res.data.success) {
        setOrders(res.data.orders);
        setTotalPages(res.data.pages || 1);
        setTotalOrders(res.data.total || res.data.orders.length);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setPage(1);
  };

  const getTrackingUrl = (order) => {
    return `${window.location.origin}/commande/${order.tracking_token}`;
  };

  const copyTrackingLink = (order, e) => {
    if (e) e.stopPropagation();
    const url = getTrackingUrl(order);
    navigator.clipboard.writeText(url);
    showToast('Lien de suivi copié !', 'success');
  };

  const sendTrackingWhatsApp = (order, e) => {
    if (e) e.stopPropagation();
    const url = getTrackingUrl(order);
    const message = encodeURIComponent(
      `Bonjour ${order.customer_name} ! 🛍️\n\n` +
      `Votre commande #${order.order_number} a été enregistrée.\n\n` +
      `📦 Suivez votre commande ici :\n${url}\n\n` +
      `Merci pour votre confiance !`
    );
    const phone = order.customer_phone.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  // Informations sur les statuts
  const statusInfo = {
    confirmed: {
      label: 'Confirmer la commande',
      icon: '✓',
      color: 'red',
      description: 'La commande sera marquée comme confirmée. Le client sera informé que vous avez bien reçu sa commande et que vous allez la traiter.',
      warning: '⚠️ Attention : une fois confirmée, le stock des produits sera retiré du stock disponible de la boutique.'
    },
    preparing: {
      label: 'Mettre en préparation',
      icon: '🔧',
      color: 'purple',
      description: 'La commande passe en préparation. Le client sera informé que sa commande est en cours de préparation.',
      warning: null
    },
    delivered: {
      label: 'Marquer comme livrée',
      icon: '✅',
      color: 'green',
      description: 'La commande sera marquée comme livrée avec succès.',
      warning: '⚠️ Attention : Une fois livrée, la commande ne pourra plus être annulée. Et le montant sera ajouté aux revenus de la boutique dans paiements.'
    },
    cancelled: {
      label: 'Annuler la commande',
      icon: '❌',
      color: 'red',
      description: 'La commande sera annulée. Le client sera informé de cette annulation.',
      warning: '⚠️ Attention : Cette action est irréversible. Le stock des produits sera restauré.'
    }
  };

  // Ouvrir le modal de confirmation
  const openConfirmModal = (orderId, newStatus, currentStatus) => {
    setConfirmModal({
      orderId,
      newStatus,
      currentStatus,
      reason: ''
    });
  };

  // Fermer le modal de confirmation
  const closeConfirmModal = () => {
    setConfirmModal(null);
  };

  // Confirmer le changement de statut
  const confirmStatusChange = async () => {
    if (!confirmModal) return;
    await updateStatus(confirmModal.orderId, confirmModal.newStatus, confirmModal.reason);
    closeConfirmModal();
  };

  const updateStatus = async (orderId, newStatus, reason = null) => {
    setUpdating(true);
    try {
      const res = await api.put(`/shop/orders/${orderId}/status`, { 
        status: newStatus,
        reason: reason 
      });
      if (res.data.success) {
        // Mettre à jour l'ordre dans la liste
        setOrders(prev => prev.map(o => 
          o.id === orderId ? res.data.order : o
        ));
        if (selectedOrder?.id === orderId) {
          setSelectedOrder(res.data.order);
        }
        showToast(res.data.message, 'success');
      } else {
        showToast(res.data.message, 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Erreur lors de la mise à jour', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const openOrderModal = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      confirmed: 'bg-blue-100 text-blue-800',
      preparing: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmée',
      preparing: 'En préparation',
      delivered: 'Livrée',
      cancelled: 'Annulée'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getDeliveryBadge = (type) => {
    if (type === 'delivery') {
      return <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">🚚 Livraison</span>;
    }
    return <span className="text-xs bg-gray-50 text-gray-600 px-2 py-0.5 rounded">🏠 Retrait</span>;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${BACKEND_URL}/${path}`;
  };

  const getNextStatus = (currentStatus) => {
    const flow = {
      pending: 'confirmed',
      confirmed: 'preparing',
      preparing: 'delivered'
    };
    return flow[currentStatus] || null;
  };

  const getNextStatusLabel = (currentStatus) => {
    const labels = {
      pending: 'Confirmer',
      confirmed: 'En préparation',
      preparing: 'Marquer livrée'
    };
    return labels[currentStatus] || null;
  };

  // Écran de chargement
  if (loading && !hasAccess) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Accès refusé - pas le module shop
  if (!hasAccess) {
    return (
      <div className="p-4 md:p-8">
        <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">📦</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Commandes Boutique</h1>
          <p className="text-gray-600 mb-6">
            La gestion des commandes est disponible uniquement avec l'abonnement <strong>Professionnel</strong>.
          </p>
          
          <div className="space-y-3">
            <Link
              to="/my-subscription"
              className="block w-full px-6 py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition"
            >
              📦 Passer à Professionnel
            </Link>
            <button
              onClick={() => navigate('/workspace')}
              className="block w-full px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition"
            >
              ← Retour au tableau de bord
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">📦 Commandes</h1>
          <p className="text-gray-500 text-sm">Gérez les commandes de votre boutique</p>
        </div>
        <Link
          to={`${routeBase}/shop`}
          className="inline-flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border rounded-lg hover:bg-gray-50 transition"
        >
          ← Configuration boutique
        </Link>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-2 mb-4 overflow-x-auto pb-2">
        {['all', 'pending', 'confirmed', 'preparing', 'delivered', 'cancelled'].map(status => (
          <button
            key={status}
            onClick={() => { setStatusFilter(status); setPage(1); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${
              statusFilter === status 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {status === 'all' && 'Toutes'}
            {status === 'pending' && '⏳ En attente'}
            {status === 'confirmed' && '✓ Confirmées'}
            {status === 'preparing' && '🔧 En préparation'}
            {status === 'delivered' && '✅ Livrées'}
            {status === 'cancelled' && '❌ Annulées'}
          </button>
        ))}
      </div>

      {/* Barre de recherche */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Rechercher par n° commande, nom ou téléphone..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">🔍</span>
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            Rechercher
          </button>
          {searchQuery && (
            <button
              type="button"
              onClick={clearSearch}
              className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
            >
              ✕ Effacer
            </button>
          )}
        </form>
        {searchQuery && (
          <div className="mt-2 text-sm text-gray-500">
            Résultats pour "{searchQuery}" : {totalOrders} commande(s) trouvée(s)
          </div>
        )}
      </div>

      {/* Liste des commandes */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600"></div>
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="text-4xl mb-4">📭</div>
          <h3 className="text-lg font-medium text-gray-800 mb-2">Aucune commande</h3>
          <p className="text-gray-500">
            {statusFilter === 'all' 
              ? 'Vous n\'avez pas encore reçu de commandes'
              : 'Aucune commande avec ce statut'
            }
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {orders.map(order => (
              <div 
                key={order.id}
                className="bg-white rounded-xl shadow-sm p-4 hover:shadow-md transition cursor-pointer"
                onClick={() => openOrderModal(order)}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  {/* Info commande */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-bold text-gray-800">#{order.order_number}</span>
                      {getStatusBadge(order.status)}
                      {getDeliveryBadge(order.delivery_type)}
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">{order.customer_name}</span>
                      <span className="mx-2">•</span>
                      <span>{order.customer_phone}</span>
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {formatDate(order.created_at)}
                    </div>
                  </div>

                  {/* Montant et actions */}
                  <div className="flex items-center justify-between md:justify-end gap-2 md:gap-4">
                    <div className="text-right">
                      <div className="font-bold text-lg text-gray-800">
                        {parseInt(order.total_amount).toLocaleString()} FCFA
                      </div>
                      <div className="text-xs text-gray-500">
                        {order.items?.length || 0} article(s)
                      </div>
                    </div>

                    {/* Boutons lien de suivi */}
                    <div className="flex gap-1">
                      <button
                        onClick={(e) => copyTrackingLink(order, e)}
                        title="Copier le lien de suivi"
                        className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                      >
                        📋
                      </button>
                      <button
                        onClick={(e) => sendTrackingWhatsApp(order, e)}
                        title="Envoyer par WhatsApp"
                        className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                      >
                        💬
                      </button>
                    </div>
                    
                    {/* Boutons d'action - seulement si pas livré ou annulé */}
                    {order.status !== 'delivered' && order.status !== 'cancelled' && (
                      <div className="flex gap-2">
                        {getNextStatus(order.status) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openConfirmModal(order.id, getNextStatus(order.status), order.status);
                            }}
                            disabled={updating}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium transition"
                          >
                            {getNextStatusLabel(order.status)}
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openConfirmModal(order.id, 'cancelled', order.status);
                          }}
                          disabled={updating}
                          className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 disabled:opacity-50 text-sm font-medium transition"
                          title="Annuler la commande"
                        >
                          ❌
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                ← Précédent
              </button>
              <span className="px-4 py-2 text-gray-600">
                Page {page} sur {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Suivant →
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal détail commande */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-bold text-gray-800">
                  Commande #{selectedOrder.order_number}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  {getStatusBadge(selectedOrder.status)}
                  {getDeliveryBadge(selectedOrder.delivery_type)}
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-6">
              {/* Client */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-800 mb-2">👤 Client</h3>
                <div className="space-y-1 text-sm">
                  <div><strong>Nom:</strong> {selectedOrder.customer_name}</div>
                  <div><strong>Téléphone:</strong> {selectedOrder.customer_phone}</div>
                  {selectedOrder.customer_email && (
                    <div><strong>Email:</strong> {selectedOrder.customer_email}</div>
                  )}
                  {selectedOrder.customer_address && (
                    <div><strong>Adresse:</strong> {selectedOrder.customer_address}</div>
                  )}
                </div>
              </div>

              {/* Articles */}
              <div>
                <h3 className="font-medium text-gray-800 mb-3">🛒 Articles</h3>
                <div className="space-y-3">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                      <div className="w-12 h-12 bg-gray-200 rounded-lg overflow-hidden flex items-center justify-center">
                        {item.product_image ? (
                          <img src={getImageUrl(item.product_image)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-xl">📦</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-800">{item.product_name}</div>
                        <div className="text-sm text-gray-500">
                          {parseInt(item.product_price).toLocaleString()} × {item.quantity}
                        </div>
                      </div>
                      <div className="font-medium text-gray-800">
                        {parseInt(item.subtotal).toLocaleString()} FCFA
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totaux */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sous-total</span>
                    <span>{parseInt(selectedOrder.subtotal).toLocaleString()} FCFA</span>
                  </div>
                  {parseFloat(selectedOrder.delivery_fee) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Frais de livraison</span>
                      <span>{parseInt(selectedOrder.delivery_fee).toLocaleString()} FCFA</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                    <span>Total</span>
                    <span>{parseInt(selectedOrder.total_amount).toLocaleString()} FCFA</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedOrder.notes && (
                <div className="bg-amber-50 rounded-lg p-4">
                  <h3 className="font-medium text-amber-800 mb-2">📝 Notes du client</h3>
                  <p className="text-sm text-amber-700">{selectedOrder.notes}</p>
                </div>
              )}

              {/* Raison d'annulation */}
              {selectedOrder.status === 'cancelled' && selectedOrder.cancelled_reason && (
                <div className="bg-red-50 rounded-lg p-4">
                  <h3 className="font-medium text-red-800 mb-2">❌ Raison d'annulation</h3>
                  <p className="text-sm text-red-700">{selectedOrder.cancelled_reason}</p>
                </div>
              )}

              {/* Timeline */}
              <div>
                <h3 className="font-medium text-gray-800 mb-3">📅 Historique</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                    <span className="text-gray-600">Créée le {formatDate(selectedOrder.created_at)}</span>
                  </div>
                  {selectedOrder.confirmed_at && (
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                      <span className="text-gray-600">Confirmée le {formatDate(selectedOrder.confirmed_at)}</span>
                    </div>
                  )}
                  {selectedOrder.preparing_at && (
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                      <span className="text-gray-600">En préparation le {formatDate(selectedOrder.preparing_at)}</span>
                    </div>
                  )}
                  {selectedOrder.delivered_at && (
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      <span className="text-gray-600">Livrée le {formatDate(selectedOrder.delivered_at)}</span>
                    </div>
                  )}
                  {selectedOrder.cancelled_at && (
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                      <span className="text-gray-600">Annulée le {formatDate(selectedOrder.cancelled_at)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              {selectedOrder.status !== 'delivered' && selectedOrder.status !== 'cancelled' && (
                <div className="border-t pt-4">
                  <div className="flex flex-wrap gap-3">
                    {/* Bouton pour passer au statut suivant */}
                    {getNextStatus(selectedOrder.status) && (
                      <button
                        onClick={() => openConfirmModal(selectedOrder.id, getNextStatus(selectedOrder.status), selectedOrder.status)}
                        disabled={updating}
                        className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium transition"
                      >
                        {updating ? '...' : getNextStatusLabel(selectedOrder.status)}
                      </button>
                    )}
                    
                    {/* Bouton d'annulation disponible à toutes les étapes sauf livré */}
                    <button
                      onClick={() => openConfirmModal(selectedOrder.id, 'cancelled', selectedOrder.status)}
                      disabled={updating}
                      className="flex-1 px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 disabled:opacity-50 font-medium transition"
                    >
                      ❌ Annuler
                    </button>
                  </div>
                </div>
              )}

              {/* Contact WhatsApp */}
              {selectedOrder.customer_phone && (
                <div className="border-t pt-4 space-y-3">
                  <h3 className="font-medium text-gray-800">🔗 Lien de suivi</h3>
                  
                  {/* Afficher le lien */}
                  <div className="bg-gray-50 rounded-lg p-3 text-sm break-all text-gray-600">
                    {getTrackingUrl(selectedOrder)}
                  </div>
                  
                  {/* Boutons d'action */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => copyTrackingLink(selectedOrder)}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition font-medium"
                    >
                      📋 Copier le lien
                    </button>
                    <button
                      onClick={() => sendTrackingWhatsApp(selectedOrder)}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition font-medium"
                    >
                      💬 Envoyer par WhatsApp
                    </button>
                  </div>
                  
                  {/* Contact direct WhatsApp */}
                  <a
                    href={`https://wa.me/${selectedOrder.customer_phone.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full px-4 py-2 border border-green-500 text-green-600 rounded-lg hover:bg-green-50 transition"
                  >
                    <span>📞</span> Contacter le client
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Modal de confirmation de changement de statut */}
      {confirmModal && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={closeConfirmModal}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <style>{`
              @keyframes modalScale {
                from { transform: scale(0.9); opacity: 0; }
                to { transform: scale(1); opacity: 1; }
              }
              .animate-modal { animation: modalScale 0.2s ease-out; }
            `}</style>
            
            {/* Header avec icône */}
            <div className={`p-6 text-center ${
              confirmModal.newStatus === 'cancelled' ? 'bg-red-50' :
              confirmModal.newStatus === 'delivered' ? 'bg-green-50' :
              confirmModal.newStatus === 'preparing' ? 'bg-purple-50' :
              'bg-blue-50'
            }`}>
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center text-3xl ${
                confirmModal.newStatus === 'cancelled' ? 'bg-red-100' :
                confirmModal.newStatus === 'delivered' ? 'bg-green-100' :
                confirmModal.newStatus === 'preparing' ? 'bg-purple-100' :
                'bg-blue-100'
              }`}>
                {statusInfo[confirmModal.newStatus]?.icon}
              </div>
              <h3 className={`mt-4 text-xl font-bold ${
                confirmModal.newStatus === 'cancelled' ? 'text-red-800' :
                confirmModal.newStatus === 'delivered' ? 'text-green-800' :
                confirmModal.newStatus === 'preparing' ? 'text-purple-800' :
                'text-blue-800'
              }`}>
                {statusInfo[confirmModal.newStatus]?.label}
              </h3>
            </div>
            
            {/* Contenu */}
            <div className="p-6 space-y-4">
              {/* Description */}
              <p className="text-gray-600 text-center">
                {statusInfo[confirmModal.newStatus]?.description}
              </p>
              
              {/* Avertissement */}
              {statusInfo[confirmModal.newStatus]?.warning && (
                <div className={`p-3 rounded-lg text-sm ${
                  confirmModal.newStatus === 'cancelled' ? 'bg-red-50 text-red-700' :
                  confirmModal.newStatus === 'delivered' ? 'bg-amber-50 text-amber-700' :
                  'bg-gray-50 text-gray-700'
                }`}>
                  {statusInfo[confirmModal.newStatus]?.warning}
                </div>
              )}
              
              {/* Champ de raison pour l'annulation */}
              {confirmModal.newStatus === 'cancelled' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Raison d'annulation (optionnel)
                  </label>
                  <textarea
                    value={confirmModal.reason}
                    onChange={(e) => setConfirmModal(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Ex: Produit en rupture de stock, client ne répond pas..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    rows={3}
                  />
                </div>
              )}
            </div>
            
            {/* Boutons */}
            <div className="flex gap-3 p-6 pt-0">
              <button
                onClick={closeConfirmModal}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition"
              >
                Annuler
              </button>
              <button
                onClick={confirmStatusChange}
                disabled={updating}
                className={`flex-1 px-4 py-3 text-white font-medium rounded-xl transition disabled:opacity-50 ${
                  confirmModal.newStatus === 'cancelled' ? 'bg-red-600 hover:bg-red-700' :
                  confirmModal.newStatus === 'delivered' ? 'bg-green-600 hover:bg-green-700' :
                  confirmModal.newStatus === 'preparing' ? 'bg-purple-600 hover:bg-purple-700' :
                  'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {updating ? 'Mise à jour...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessShopOrders;
